// src/lib/invoices.js — Phase 3 PR 6 (ADR-066)
// Helpers client Supabase pour génération + gestion factures + avoirs.
//
// Architecture révisée (mega session 2026-05-07) :
//   - Voyageurs : numéro séquentiel inviolable, status='sent' immutable,
//     soft-delete uniquement si draft. PAS d'avoirs (plateformes gèrent).
//   - Proprio mensuelles : numéro inviolable + système avoirs simple.
//
// Numéros : {PREFIX}-{YEAR}-{NNNN} (ex: P-2026-0001, V-2026-0001, AVE-2026-0001)
// Génération atomique via RPC SECURITY DEFINER `next_invoice_number()`.

import { supabase } from "../supabase"
import { INVOICE_NUMBER_PLACEHOLDER, INVOICE_DATE_PLACEHOLDER } from "./facture-html"

// Préfixes de séries par émetteur (décisions Dimitri verrouillées) :
//   - 'P' : FBZ SAS → commissions mensuelles proprios (émetteur = FBZ).
//   - 'V' : FBZ SAS → voyageurs Maison Zen (sous-loc p6).
//   - {prop.invoice_prefix} (AVE/KHA/POU/LAN/BOD…) : proprio → voyageur.
// Plus de fallback 'FRZ' : un préfixe proprio manquant doit throw (le seed des
// préfixes est fait séparément, gaté).
export const PREFIX_COMMISSION = 'P'
export const PREFIX_VOYAGEUR_FBZ = 'V'

/**
 * Libellé FR d'un type de document (BUG 37 — ne pas afficher la valeur brute) :
 *   'receipt' → « Reçu », 'invoice' → « Facture », 'credit_note' → « Avoir ».
 * Fallback : la valeur brute (ou '—') pour ne rien masquer.
 */
export function invoiceTypeLabel(invoiceType) {
  return ({ receipt: 'Reçu', invoice: 'Facture', credit_note: 'Avoir' }[invoiceType]) || invoiceType || '—'
}

/**
 * Libellé du document PRÉ-émission, dérivé du logement courant (avant qu'un
 * `invoice_type` existe en base) : un proprio émet un « Reçu », la sous-loc
 * Maison Zen (série V, prop.id === 'p6') émet une « Facture ». Source unique de
 * la règle p6 → pas de string 'p6' copiée à chaque label.
 *   - opts.lower : variante minuscule (« reçu » / « facture ») pour les phrases.
 */
export function docLabelForProp(prop, opts = {}) {
  const isSousLoc = prop?.id === 'p6'
  const label = isSousLoc ? 'Facture' : 'Reçu'
  return opts.lower ? label.toLowerCase() : label
}

/**
 * Génère atomiquement le prochain numéro de facture pour (prefix, year).
 * RPC SECURITY DEFINER pose un row lock UPSERT — pas de doublons concurrents.
 *
 * @param {string} prefix - ex: 'P', 'V', 'AVE', 'AV-P'
 * @param {number} year   - ex: 2026
 * @returns {Promise<string>} - ex: 'P-2026-0001'
 */
export async function nextInvoiceNumber(prefix, year) {
  if (!prefix) throw new Error('prefix requis')
  if (!year || year < 2020 || year > 2100) throw new Error(`year invalide : ${year}`)

  const { data, error } = await supabase.rpc('next_invoice_number', {
    p_prefix: prefix,
    p_year: year,
  })
  if (error) throw new Error(`next_invoice_number failed : ${error.message}`)
  if (!data) throw new Error('next_invoice_number a retourné null')
  return data
}

/**
 * Crée une facture en 1 INSERT atomique avec html_snapshot + amount complets.
 * Le numéro doit être obtenu au préalable via nextInvoiceNumber() pour permettre
 * la génération du HTML AVEC le numéro réel avant INSERT — évite les UPDATE
 * post-INSERT bloqués par RLS (fix F-JUR-1 Pessimist QA deep PR 6).
 *
 * SNAPSHOT FIGÉ (BUG 14 — référence vivante vs copie figée) :
 *   `html_snapshot` est la COPIE FIGÉE du document au moment de l'émission.
 *   Les noms (proprio/logement/émetteur) qu'il contient ont été résolus EN AMONT
 *   par l'appelant (genFactureResaHTML / genFactureHTML) à partir du cfg COURANT,
 *   puis gelés ici. Cette fonction n'écrit `html_snapshot` qu'à l'INSERT et ne le
 *   réécrit JAMAIS : un renommage proprio/logement ultérieur ne doit pas altérer
 *   un document déjà émis (CGI 289 : pièce comptable irréversible). Aucun chemin
 *   d'UPDATE de cette colonne n'existe (ni ici, ni dans invoice-handlers.js qui
 *   ne patche que status/delivered_at).
 *   Pour que l'HISTORIQUE (liste) affiche aussi le nom À L'ÉMISSION sans relire
 *   le snapshot lourd, l'appelant doit figer les libellés dans `meta`
 *   (ex. { log_nom, prop_nom }) — eux aussi snapshot, jamais réécrits.
 *
 * @param {object} args
 * @param {string} args.invoice_number - issu de nextInvoiceNumber(prefix, year)
 * @param {string} args.prefix         - prefix séquence (ex: 'FRZ')
 * @param {string} args.invoice_type   - 'invoice' (par défaut), 'receipt' (reçu
 *                                       voyageur au nom du proprio, B1) ou
 *                                       'credit_note' (avoir : utiliser
 *                                       cancelInvoiceByCreditNote). La séquence et
 *                                       l'INSERT sont type-agnostiques : 'receipt'
 *                                       consomme la même série {prefix} qu'une
 *                                       facture, related_invoice_id reste NULL.
 * @param {string} args.emitter_type   - 'fbz' | 'proprio'
 * @param {string} [args.emitter_id]   - prop.id si proprio
 * @param {string} [args.reservation_id]
 * @param {string} [args.logement_id]
 * @param {number} [args.amount]       - montant final (calculé client après genHTML)
 * @param {string} [args.html_snapshot] - HTML complet pour archivage CGI 289
 * @param {object} [args.meta]
 * @param {string} [args.status]       - 'draft' (par défaut) ou 'sent'
 * @returns {Promise<{id, invoice_number, ...}>}
 */
export async function createInvoice(args) {
  const {
    invoice_number,
    prefix,
    invoice_type = 'invoice',
    emitter_type,
    emitter_id = null,
    reservation_id = null,
    logement_id = null,
    amount = null,
    html_snapshot = null,
    meta = null,
    status = 'draft',
  } = args
  if (!invoice_number) throw new Error('invoice_number requis (utiliser nextInvoiceNumber() avant)')
  if (!prefix) throw new Error('prefix requis')
  if (!emitter_type || !['fbz', 'proprio'].includes(emitter_type)) {
    throw new Error(`emitter_type invalide : ${emitter_type}`)
  }
  // 'receipt' (reçu voyageur proprio, B1) est autorisé ici comme 'invoice' :
  // même série {prefix}, même INSERT, related_invoice_id NULL. Seul 'credit_note'
  // (avoir) est refusé — il passe par cancelInvoiceByCreditNote (RPC dédiée).
  if (invoice_type === 'credit_note') {
    throw new Error("Utiliser cancelInvoiceByCreditNote() pour créer un avoir.")
  }

  const year = new Date().getFullYear()

  const insertPayload = {
    invoice_number,
    invoice_type,
    related_invoice_id: null,
    reservation_id,
    logement_id,
    emitter_type,
    emitter_id,
    prefix,
    year,
    amount,
    currency: 'EUR',
    status,
    html_snapshot,
    meta,
    sent_at: status === 'sent' ? new Date().toISOString() : null,
  }

  const { data, error } = await supabase.from('invoices').insert(insertPayload).select().single()
  if (error) throw new Error(`createInvoice failed : ${error.message}`)
  return data
}

// ── Chantier « numéro attribué À L'ENVOI » ───────────────────────────────
//
// Incident 2026-08-04 : une facture consommait son numéro dès la CRÉATION. La
// supprimer alors qu'elle n'était pas la dernière perdait définitivement son
// numéro (resync_invoice_sequence recale sur le MAX PRÉSENT) → trou dans la
// série, interdit par l'obligation de numérotation continue.
//
// Le brouillon corrige la cause : tant que rien n'est envoyé, AUCUN numéro n'est
// consommé. La pièce est supprimable/regénérable à volonté sans conséquence sur
// la série. Le numéro est tiré au passage à 'sent', par la RPC atomique
// assign_invoice_number() appelée CÔTÉ SERVEUR (action setInvoiceDelivered) —
// la RPC n'est volontairement PAS exposée au rôle `authenticated`.

/**
 * Crée un BROUILLON de facture : status='draft', invoice_number NULL.
 *
 * Différences avec createInvoice() (qui reste le chemin des émissions en un seul
 * geste — création ET transmission simultanées) :
 *   - AUCUN appel à next_invoice_number() : rien n'est consommé ;
 *   - `html_snapshot` DOIT porter le jeton `__INVOICE_NUMBER__` (généré par
 *     genFactureHTML({ draft: true })). Sans lui, assign_invoice_number()
 *     refuserait plus tard l'attribution (fail-closed) : on échoue donc ICI,
 *     tout de suite, plutôt que de laisser un brouillon inémissible en base.
 *
 * @param {object} args - mêmes champs que createInvoice, SANS invoice_number ni status
 * @returns {Promise<object>} la ligne invoices insérée (invoice_number === null)
 */
export async function createDraftInvoice(args) {
  const {
    prefix,
    invoice_type = 'invoice',
    emitter_type,
    emitter_id = null,
    reservation_id = null,
    logement_id = null,
    amount = null,
    html_snapshot = null,
    meta = null,
  } = args || {}

  if (!prefix) throw new Error('prefix requis')
  if (!emitter_type || !['fbz', 'proprio'].includes(emitter_type)) {
    throw new Error(`emitter_type invalide : ${emitter_type}`)
  }
  if (invoice_type === 'credit_note') {
    throw new Error("Utiliser cancelInvoiceByCreditNote() pour créer un avoir.")
  }
  // Les DEUX jetons sont exigés : le numéro ET la date sont posés à
  // l'attribution (option A). Un brouillon sans jeton date produirait un
  // document définitif sans date d'émission — on échoue ICI, à la création,
  // plutôt que de laisser découvrir le problème au clic « envoyé ».
  // NB : la garde SQL (assign_invoice_number) ne teste QUE le jeton numéro,
  // volontairement — durcir côté base rendrait inémissible un éventuel brouillon
  // créé avant ce changement. Le fail-closed strict vit côté producteur.
  for (const jeton of [INVOICE_NUMBER_PLACEHOLDER, INVOICE_DATE_PLACEHOLDER]) {
    if (!html_snapshot || !html_snapshot.includes(jeton)) {
      throw new Error(
        `createDraftInvoice : html_snapshot doit porter le jeton ${jeton} (générer via genFactureHTML({ draft: true }))`,
      )
    }
  }

  // ⚠️ ANNÉE PROVISOIRE. La colonne `year` est NOT NULL, il faut donc écrire
  // quelque chose — mais ce n'est PAS l'année de série. assign_invoice_number()
  // la RECALCULE à l'attribution (option A, décision propriétaire 2026-08-05 :
  // la pièce est émise le jour où elle reçoit son numéro). Un brouillon créé le
  // 20/12 et envoyé le 05/01 sera donc classé dans la série de l'année suivante,
  // avec `year` mis à jour en même temps que le numéro.
  // NE PAS se fier à `year` sur une ligne status='draft'.
  const year = new Date().getFullYear()

  const insertPayload = {
    invoice_number: null, // ⟸ AUCUN numéro consommé tant que rien n'est envoyé
    invoice_type,
    related_invoice_id: null,
    reservation_id,
    logement_id,
    emitter_type,
    emitter_id,
    prefix,
    year,
    amount,
    currency: 'EUR',
    status: 'draft',
    html_snapshot,
    meta,
    sent_at: null,
  }

  const { data, error } = await supabase.from('invoices').insert(insertPayload).select().single()
  if (error) {
    // On PROPAGE le code Postgres (calqué sur createReceiptServer,
    // lib/invoice-emit.js) : l'appelant doit pouvoir distinguer le cas
    // `23502 not_null_violation` — qui signifie « migration 20261115000001 pas
    // encore appliquée, invoice_number est toujours NOT NULL » — d'une vraie
    // panne, pour afficher un message actionnable au lieu d'une erreur SQL brute.
    const err = new Error(`createDraftInvoice failed : ${error.message}`)
    err.code = error.code
    throw err
  }
  return data
}

/**
 * Une ligne `invoices` est-elle un brouillon ? Source unique de la règle.
 * Le critère porte sur l'ABSENCE DE NUMÉRO, pas sur le statut : c'est le numéro
 * qui a une valeur probante, et l'invariant DB
 * (invoices_number_required_when_emitted) garantit qu'une pièce émise en a un.
 */
export function isDraftInvoice(row) {
  return !!row && !row.invoice_number
}

/**
 * Libellé à afficher pour une ligne `invoices` : le numéro réel, ou « Brouillon ».
 * Jamais de numéro factice — un brouillon doit se voir comme tel.
 */
export function invoiceDisplayNumber(row) {
  return isDraftInvoice(row) ? 'Brouillon' : row.invoice_number
}

/**
 * ⚠️ HORS SERVICE côté client — conservé pour la forme de l'API, ne plus appeler.
 *
 * Chantier « L'avoir a un bouton » (2026-09-14) : l'avoir s'émet par l'ACTION
 * SERVEUR `emitCreditNote` (api/beds24-mapping.js → lib/invoice-handlers.js,
 * requireAdmin) qui appelle la RPC v2 `emit_credit_note` (migration
 * 20270114000001) : série AV-* dérivée de la facture (jamais fournie), jetons
 * de numéro/date substitués dans la même transaction, meta écrit. La RPC v1
 * `cancel_invoice_by_credit_note` appelée ici est REVOQUÉE à `authenticated`
 * par cette même migration : cet appel échouera. Voir src/lib/invoice-avoir-flow.js.
 *
 * @param {object} args
 * @param {string} args.invoice_id      - id facture à annuler
 * @param {string} args.credit_prefix   - prefix séquence avoir (ex: 'AV-FRZ')
 * @param {string} [args.html_snapshot] - HTML snapshot avoir pour archivage
 * @returns {Promise<string>} - id de l'avoir créé
 */
export async function cancelInvoiceByCreditNote(args) {
  const { invoice_id, credit_prefix, html_snapshot = null } = args
  if (!invoice_id) throw new Error('invoice_id requis')
  if (!credit_prefix) throw new Error('credit_prefix requis')

  const { data, error } = await supabase.rpc('cancel_invoice_by_credit_note', {
    p_invoice_id: invoice_id,
    p_credit_prefix: credit_prefix,
    p_html_snapshot: html_snapshot,
  })
  if (error) throw new Error(`cancelInvoiceByCreditNote failed : ${error.message}`)
  return data
}

/**
 * Récupère le html_snapshot archivé d'une facture depuis la table invoices.
 * Fallback du bouton « voir facture » (chantier Pass A, Point 5) quand le
 * factLog KV ne porte pas de htmlSnapshot mais possède un invoiceId.
 *
 * Lecture seule, autorisée par la policy RLS `invoices_select_authenticated`.
 * Aucune édition. Renvoie le HTML compressé brut (à passer à
 * inflateFactureSnapshot côté appelant) ou null si absent.
 *
 * @param {string} invoiceId - invoices.id (UUID)
 * @returns {Promise<string|null>}
 */
export async function fetchInvoiceSnapshot(invoiceId) {
  if (!invoiceId) return null
  const { data, error } = await supabase
    .from('invoices')
    .select('html_snapshot')
    .eq('id', invoiceId)
    .maybeSingle()
  if (error) {
    console.error('[fetchInvoiceSnapshot]', error)
    return null
  }
  return data?.html_snapshot || null
}

/**
 * Liste les factures liées à une réservation (1 facture initiale + N avoirs si annulée).
 */
export async function listInvoicesByReservation(reservation_id) {
  const { data, error } = await supabase
    .from('invoices')
    .select('id, invoice_number, invoice_type, related_invoice_id, status, amount, currency, sent_at, cancelled_at, created_at')
    .eq('reservation_id', reservation_id)
    .order('created_at', { ascending: false })
  if (error) throw new Error(`listInvoicesByReservation failed : ${error.message}`)
  return data || []
}

/**
 * Liste les factures ACTIVES pour l'historique « Factures émises ». Source
 * fiscale = table invoices (RLS invoices_select_authenticated=true → lecture
 * client OK). EXCLUT les avoirs (invoice_type='credit_note') et les factures
 * annulées (status='cancelled') — décision Dimitri : l'historique ne montre que
 * les factures actives (les avoirs/annulées vivent dans l'onglet Documents) →
 * Total émis lisible + pas de toggle/suppression sur une annulée.
 * html_snapshot NON chargé (lourd) : l'aperçu le récupère à la demande via
 * fetchInvoiceSnapshot(id). Trié année↓ puis numéro↓.
 */
export async function listInvoices() {
  const { data, error } = await supabase
    .from('invoices')
    .select(
      'id, invoice_number, invoice_type, status, amount, currency, sent_at, created_at, delivered_at, cancelled_at, prefix, year, logement_id, reservation_id, meta',
    )
    .neq('invoice_type', 'credit_note')
    .neq('status', 'cancelled')
    .order('year', { ascending: false })
    .order('invoice_number', { ascending: false })
  if (error) throw new Error(`listInvoices failed : ${error.message}`)
  return data || []
}

/**
 * Liste les pièces ARCHIVÉES pour l'Historique : les factures ANNULÉES par
 * avoir (status='cancelled') et les AVOIRS (invoice_type='credit_note').
 * Complément strict de listInvoices() — même colonnes, même tri — pour que
 * l'écran puisse montrer qu'un avoir n'efface rien : l'originale est toujours
 * là, à côté de sa contrepartie. Lecture seule (RLS invoices_select).
 */
export async function listInvoicesArchive() {
  const { data, error } = await supabase
    .from('invoices')
    .select(
      'id, invoice_number, invoice_type, related_invoice_id, status, amount, currency, sent_at, created_at, delivered_at, cancelled_at, prefix, year, logement_id, reservation_id, meta',
    )
    .or('invoice_type.eq.credit_note,status.eq.cancelled')
    .order('year', { ascending: false })
    .order('invoice_number', { ascending: false })
  if (error) throw new Error(`listInvoicesArchive failed : ${error.message}`)
  return data || []
}

/**
 * Liste les factures par émetteur + année (utilisé pour facture mensuelle proprio).
 */
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 ANTI-DOUBLON BORNÉ À L'ANNÉE CIVILE — corrigé le 2026-08-05
// ═══════════════════════════════════════════════════════════════════════════
// Le filtre était `.eq('year', year)` avec l'année COURANTE. Or l'écran qui
// s'en sert liste les 50 dernières réservations, qui débordent forcément sur
// l'année précédente en début d'année. Au 1er janvier, la facture d'un séjour
// de décembre n'était donc plus chargée : sa pastille « déjà facturé »
// disparaissait, et RIEN n'empêchait plus de la ré-émettre — un doublon de
// numérotation sur une pièce déjà envoyée (CGI 289).
//
// `years` (tableau) permet désormais de couvrir toutes les années réellement
// concernées. `year` (scalaire) reste accepté à l'identique pour les appelants
// existants.
export async function listInvoicesByEmitter({ emitter_type, emitter_id, year, years }) {
  // `null` et `''` se convertissent en 0 : on les écarte explicitement, sinon
  // une année « 0 » polluerait le filtre.
  const yearList = Array.isArray(years) && years.length
    ? [...new Set(
        years
          .filter(y => y !== null && y !== undefined && y !== '' && Number.isFinite(Number(y)))
          .map(Number),
      )]
    : null
  let q = supabase
    .from('invoices')
    .select('id, invoice_number, invoice_type, related_invoice_id, status, amount, currency, sent_at, cancelled_at, created_at, reservation_id, logement_id')
    .eq('emitter_type', emitter_type)
    .order('created_at', { ascending: false })
  q = yearList ? q.in('year', yearList) : q.eq('year', year)
  if (emitter_id) q = q.eq('emitter_id', emitter_id)
  const { data, error } = await q
  if (error) throw new Error(`listInvoicesByEmitter failed : ${error.message}`)
  return data || []
}

/**
 * Calcule le préfixe de série à utiliser selon le type de document.
 *
 * Séries (décisions Dimitri verrouillées) :
 *   - docType='commission_proprio' → 'P' TOUJOURS (la commission mensuelle est
 *     toujours émise par FBZ SAS, quel que soit l'émetteur passé — testé AVANT
 *     emitter). Le préfixe bailleur (AVE/KHA…) est ignoré ici.
 *   - docType='voyageur' :
 *       · emitter.type==='fbz'     → 'V'  (Maison Zen sous-loc p6).
 *       · emitter.type==='proprio' → prop.invoice_prefix (1 bailleur = 1 série) ;
 *         throw si non configuré (plus de fallback 'FRZ').
 *
 * @param {object} args
 * @param {object} args.emitter  - résolu via resolveEmitter()
 * @param {object} [args.prop]
 * @param {string} args.docType  - 'commission_proprio' | 'voyageur' (obligatoire)
 * @returns {string}
 */
export function resolveInvoicePrefix({ emitter, prop, docType }) {
  if (!docType) throw new Error('resolveInvoicePrefix : docType obligatoire')

  // La commission mensuelle est toujours émise par FBZ SAS → série 'P', AVANT
  // tout test emitter (un emitter proprio résolu ne doit pas faire basculer
  // la commission sur le préfixe bailleur).
  if (docType === 'commission_proprio') return PREFIX_COMMISSION

  if (docType === 'voyageur') {
    if (emitter?.type === 'fbz') return PREFIX_VOYAGEUR_FBZ
    if (emitter?.type === 'proprio') {
      if (!prop?.invoice_prefix) {
        throw new Error('resolveInvoicePrefix : prop.invoice_prefix manquant pour ce proprio')
      }
      return prop.invoice_prefix
    }
    throw new Error(`resolveInvoicePrefix : emitter.type invalide pour voyageur : ${emitter?.type}`)
  }

  throw new Error(`resolveInvoicePrefix : docType inconnu : ${docType}`)
}

/**
 * Calcule le préfixe pour les avoirs (dormants — hors scope fonctionnel).
 * Format : 'AV-{prefix}'. Propage docType pour ne pas throw sur la signature.
 */
export function resolveCreditNotePrefix({ emitter, prop, docType }) {
  return 'AV-' + resolveInvoicePrefix({ emitter, prop, docType })
}
